import axios, { AxiosError, AxiosInstance } from 'axios';
import { API_CONFIG } from '../constants';
import type { ApiResponse } from '../types';
import { store } from '../store';
import { logout } from '../store/slices/authSlice';
import { setSessionReplacedNotice } from '../utils/sessionNotice';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = store.getState().auth.token;
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error.response?.status === 401) {
          const data = error.response?.data as { code?: string } | undefined;
          if (data?.code === 'SESSION_REPLACED') {
            setSessionReplacedNotice();
          }
          store.dispatch(logout());
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get(url);
      return response.data;
    } catch (error) {
      throw toAppError(error);
    }
  }

  async post<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post(url, data);
      return response.data;
    } catch (error) {
      throw toAppError(error);
    }
  }

  async put<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put(url, data);
      return response.data;
    } catch (error) {
      throw toAppError(error);
    }
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete(url);
      return response.data;
    } catch (error) {
      throw toAppError(error);
    }
  }
}

export default new ApiClient();

export function toAppError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: string; code?: string } | undefined;
    if (body?.code === 'SESSION_REPLACED') {
      return new Error('You signed in on another device.');
    }
    if (error.response) {
      const msg = body?.error;
      return new Error(msg || 'Request failed');
    }
    if (error.request) {
      return new Error('Network error. Please check your connection.');
    }
    return new Error(error.message || 'An unexpected error occurred');
  }
  if (error instanceof Error) return error;
  return new Error('An unexpected error occurred');
}
